/** Keeps desktop search centred on the window, stepping aside into free toolbar space when a long title needs the room. */
import { useLayoutEffect, useRef, useState } from 'react';

interface HeaderSearchGeometry {
  viewportWidth: number;
  rowLeft: number;
  leadingRight: number;
  actionsLeft: number;
  hasTitle: boolean;
  adjacentActionWidth?: number;
  /** Controls pinned to the search's left edge (the back/forward arrows): the title stops short of them. */
  leadingAdjacentWidth?: number;
  /** The title's full unclipped width. Search leaves the window centre before it clips a title with room to spare. */
  titleWidth?: number;
}

/** Below this the preferred search width crowds the page title ("Documents" plus a project name) down to a stub. */
const TITLE_PRIORITY_BELOW = 1280;
const TITLE_PRIORITY_WIDTH = 180;
const SEARCH_MIN_WIDTH = 160;
/** Matches the cap on unified-header-centre. */
const SEARCH_MAX_WIDTH = 480;

export function getDesktopHeaderSearchLayout({ viewportWidth, rowLeft, leadingRight, actionsLeft, hasTitle, adjacentActionWidth = 0, leadingAdjacentWidth = 0, titleWidth = 0 }: HeaderSearchGeometry) {
  const centre = viewportWidth / 2;
  const gap = 12;
  const titleReserve = hasTitle ? 96 : 0;
  const preferredWidth = Math.min(SEARCH_MAX_WIDTH, Math.max(320, viewportWidth * 0.32));
  const availableWidth = 2 * Math.min(centre - leadingRight - titleReserve - gap * 2 - leadingAdjacentWidth, actionsLeft - centre - gap - adjacentActionWidth);
  const titleLimitedWidth = hasTitle && viewportWidth < TITLE_PRIORITY_BELOW
    ? 2 * (centre - leadingRight - gap * 2 - leadingAdjacentWidth - TITLE_PRIORITY_WIDTH)
    : Infinity;
  const centredWidth = Math.max(SEARCH_MIN_WIDTH, Math.min(preferredWidth, availableWidth, titleLimitedWidth));
  const titleOffset = leadingRight + gap * 2 + leadingAdjacentWidth;
  const centredLeft = centre - centredWidth / 2;
  if (titleOffset + titleWidth <= centredLeft || !hasTitle) {
    return { left: centre - rowLeft, width: centredWidth, titleMaxWidth: Math.max(0, centredLeft - titleOffset) };
  }
  const searchLeft = titleOffset + titleWidth;
  const searchRight = Math.max(centre + centredWidth / 2, Math.min(searchLeft + centredWidth, actionsLeft - gap - adjacentActionWidth));
  const width = Math.max(Math.min(SEARCH_MIN_WIDTH, centredWidth), searchRight - searchLeft);
  const left = searchRight - width;
  return {
    left: (left + searchRight) / 2 - rowLeft,
    width,
    titleMaxWidth: Math.max(0, left - titleOffset),
  };
}

export function useDesktopHeaderSearchLayout(enabled: boolean, hasTitle: boolean, adjacentActionWidth = 0, leadingAdjacentWidth = 0) {
  const [viewportWidth, setViewportWidth] = useState(() => typeof window === 'undefined' ? 0 : window.innerWidth);
  const rowRef = useRef<HTMLElement>(null);
  const leadingRef = useRef<HTMLDivElement>(null);
  const actionsRef = useRef<HTMLElement>(null);
  const updateRef = useRef<(() => void) | null>(null);

  useLayoutEffect(() => {
    const row = rowRef.current;
    const leading = leadingRef.current;
    const actions = actionsRef.current;
    if (!enabled || !row || !leading || !actions) return;

    const update = () => {
      const width = document.documentElement.clientWidth || window.innerWidth;
      setViewportWidth(previous => previous === width ? previous : width);
      const title = row.querySelector('[data-slot="unified-header-title"]');
      const layout = getDesktopHeaderSearchLayout({
        viewportWidth: width,
        rowLeft: row.getBoundingClientRect().left,
        leadingRight: leading.getBoundingClientRect().right,
        actionsLeft: actions.getBoundingClientRect().left,
        hasTitle,
        adjacentActionWidth,
        leadingAdjacentWidth,
        // scrollWidth rounds down, which would clip the last letter.
        titleWidth: title ? Math.max(0, ...Array.from(title.children, line => line.scrollWidth + 1)) : 0,
      });
      row.style.setProperty('--qs-header-search-left', `${layout.left}px`);
      row.style.setProperty('--qs-header-search-width', `${layout.width}px`);
      row.style.setProperty('--qs-header-title-max-width', `${layout.titleMaxWidth}px`);
    };
    updateRef.current = update;
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(row);
    resizeObserver.observe(leading);
    resizeObserver.observe(actions);
    // Sidebar changes can move a fixed row without changing its dimensions.
    const shell = row.closest('.qs-shell-root');
    const mutationObserver = new MutationObserver(update);
    if (shell) mutationObserver.observe(shell, { attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', update);
    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', update);
      updateRef.current = null;
      row.style.removeProperty('--qs-header-title-max-width');
    };
  }, [enabled, hasTitle, adjacentActionWidth, leadingAdjacentWidth]);

  useLayoutEffect(() => { updateRef.current?.(); });
  return { rowRef, leadingRef, actionsRef, viewportWidth };
}
